import React, { useEffect } from "react";
import { css } from "@emotion/react";
import { useNavigate } from "react-router";
import { Helmet } from "react-helmet-async";

import { useAppState } from "core/contexts/AppContext";
import useMetadata, { MetaData } from "core/hooks/useMetadata";
import useRouter from "core/hooks/useRouter";

import ErrorPage from "pages/Components/ErrorPage/ErrorPage";
import pages from "pages/config/pages";
import StripeLoader from "core/Components/Utils/StripeLoader";
import useHeaderHeight from "core/hooks/useHeaderHeight";
import { hasFeatureFlag } from "core/includes/site";
import { FLAG_SHOP_2, FLAG_SHOP_ONLINE_VIDEOS } from "core/includes/featureFlags";
import window from 'core/includes/window';

type Props = {
    stage: string | null,
    showBreadcrumb: boolean,
    hideHeaderCountry: boolean,
};

/**
 * Handles the main rendering of pages.  Returns error pages if needed.
 * @constructor
 */
const Main: React.FC<Props> = ({ stage, showBreadcrumb, hideHeaderCountry = false }) => {
    const navigate = useNavigate();
    const [appState, setAppState] = useAppState();
    const headerHeight = useHeaderHeight({ showBreadcrumb, hideHeaderCountry });

    const [data, loading, _error] = useRouter(stage);
    let error = _error;
    const page = data?.page;
    const metaData = useMetadata(page);
    const hasShopFeatureFlag = hasFeatureFlag(appState.featureFlags, FLAG_SHOP_2);
    const hasShopOnlineVideoFeatureFlag = hasFeatureFlag(appState.featureFlags, FLAG_SHOP_ONLINE_VIDEOS);

    useEffect(() => {
        // loading is used for displaying <Footer>
        setAppState({ loading });
    }, [loading, setAppState]);

    if (page?.__typename === 'RedirectorPage') {
        if (page.linkUrl.indexOf('http') === 0) {
            window.location = page.linkUrl;
        } else {
            navigate(page.linkUrl);
        }
        return null;
    }

    let Component = page && pages.hasOwnProperty(page.__typename) && pages[page.__typename];
    const isProductListPage = page && page.__typename === "ProductListPage";

    // Disallow access to ProductPage without correct feature flag
    if (!hasShopFeatureFlag && page && page.__typename === "ProductPage") error = "404";
    // Disallow access to OnlineEventPage without correct feature flag
    if (!hasShopOnlineVideoFeatureFlag && page && page.__typename === "OnlineEventPage") error = "404";

    const style = css`
        padding-top: ${headerHeight}px;
    `;

    return (
        <div css={style}>
            {(error || (page && !page.isClickable)) && (
                <ErrorPage error={error === "404" ? error : "500"} />
            )}
            {(!error && page && page.isClickable && Component) && (
                <>
                    <Helmet>
                        {metaData.map((meta: MetaData, index) => {
                            switch (meta.element) {
                                case 'link': return <link key={`meta-key-${index}`} {...meta.props} />;
                                case 'title': return <title key={`meta-key-${index}`} {...meta.props} />;
                                default: return <meta key={`meta-key-${index}`} {...meta.props} />;
                            }
                        })}
                    </Helmet>

                    {isProductListPage ? (
                        <StripeLoader>
                            <Component {...page && { data: page }} />
                        </StripeLoader>
                    ) : <Component {...page && { data: page }} />}
                </>
            )}
        </div>
    );
};

export default Main;
